/** Phone message actions (docs/mobile-design.md §4.6.3): long-press or ⋯ opens this sheet: quick reactions, then large rows. */
import { CheckSquare, Copy, Forward, Languages, Link2, MessageSquareReply, Pencil, Pin, ShieldCheck, Trash2, Truck, CircleCheck, Flag, type LucideIcon } from 'lucide-react';
import clsx from 'clsx';
import { MESSAGE_SHEET_LABEL, type MessageSheetAction } from '@tradeoutlet/shared';
import { BottomSheet } from './BottomSheet';

const ICON: Record<MessageSheetAction, LucideIcon> = {
  reply: MessageSquareReply, copy: Copy, forward: Forward, task: CheckSquare, ack: ShieldCheck, translate: Languages, pin: Pin, link: Link2,
  edit: Pencil, delete: Trash2, journey: Truck, complete: CircleCheck, report: Flag,
};

export function MessageSheet({ title, emoji, reacted, onReact, actions, labels, onAction, onClose }: {
  title: string; emoji: string[]; reacted: (e: string) => boolean; onReact: (e: string) => void;
  actions: MessageSheetAction[]; labels?: Partial<Record<MessageSheetAction, string>>; onAction: (a: MessageSheetAction) => void; onClose: () => void;
}) {
  return (
    <BottomSheet open onClose={onClose} title={title} testId="message-sheet">
      {/* t258u: no reactions on your own comments, so an empty list leaves the row out. */}
      {emoji.length > 0 && <div className="flex justify-between gap-1 px-3 pb-2" role="group" aria-label="React">
        {emoji.map(e => (
          <button key={e} type="button" aria-label={`React ${e}`} aria-pressed={reacted(e)} onClick={() => { onReact(e); onClose(); }}
            className={clsx('flex h-11 w-11 items-center justify-center rounded-full text-xl active:bg-slate-200 dark:active:bg-slate-700', reacted(e) ? 'bg-slate-200 ring-1 ring-black dark:bg-slate-700 dark:ring-white' : 'bg-slate-100 dark:bg-slate-800')}>{e}</button>
        ))}
      </div>}
      <ul className={clsx(emoji.length > 0 && 'border-t', 'border-slate-100 dark:border-slate-800')}>
        {actions.map(a => {
          const Icon = ICON[a];
          return (
            <li key={a}>
              <button type="button" data-testid={`sheet-${a}`} onClick={() => { onClose(); onAction(a); }}
                className={clsx('flex min-h-[48px] w-full items-center gap-3 px-4 text-left text-[15px] active:bg-slate-100 dark:active:bg-slate-800', a === 'delete' ? 'text-rose-700 dark:text-rose-400' : 'text-slate-800 dark:text-slate-100')}>
                <Icon className="h-5 w-5 shrink-0 text-current opacity-80" />{labels?.[a] ?? MESSAGE_SHEET_LABEL[a]}
              </button>
            </li>
          );
        })}
      </ul>
    </BottomSheet>
  );
}
